// Language detection and multilingual processing utilities
import { supabase } from '@/integrations/supabase/client';

export interface LanguageDetectionResult {
  detectedLanguage: string;
  confidence: number;
  supportedLanguages: string[];
  regionalStandards?: string[];
}

// Common HACCP terms in different languages for pattern matching
export const MULTILINGUAL_HACCP_TERMS = {
  temperature: {
    en: ['temperature', 'temp', 'degrees', 'celsius', 'fahrenheit', '°c', '°f'],
    no: ['temperatur', 'temp', 'grader', 'celsius', '°c'],
    sv: ['temperatur', 'temp', 'grader', 'celsius', '°c'],
    da: ['temperatur', 'temp', 'grader', 'celsius', '°c'],
    fi: ['lämpötila', 'asteet', 'celsius', '°c'],
    de: ['temperatur', 'temp', 'grad', 'celsius', '°c'],
    fr: ['température', 'temp', 'degrés', 'celsius', '°c']
  },
  cleaning: {
    en: ['cleaning', 'clean', 'sanitize', 'sanitizing', 'wash', 'washing', 'disinfect'],
    no: ['rengjøring', 'rens', 'desinfisering', 'vask', 'vaske'],
    sv: ['rengöring', 'rengör', 'desinfektion', 'tvätt', 'tvätta'],
    da: ['rengøring', 'rens', 'desinfektion', 'vask', 'vaske'],
    fi: ['siivous', 'siivota', 'desinfiointi', 'pesu', 'pestä'],
    de: ['reinigung', 'reinigen', 'desinfizierung', 'waschen'],
    fr: ['nettoyage', 'nettoyer', 'désinfection', 'laver']
  },
  time: {
    en: ['time', 'hour', 'minute', 'daily', 'weekly', 'hourly'],
    no: ['tid', 'time', 'minutt', 'daglig', 'ukentlig', 'hver time'],
    sv: ['tid', 'timme', 'minut', 'dagligen', 'veckovis', 'varje timme'],
    da: ['tid', 'time', 'minut', 'dagligt', 'ugentligt', 'hver time'],
    fi: ['aika', 'tunti', 'minuutti', 'päivittäin', 'viikoittain', 'tunnissa'],
    de: ['zeit', 'stunde', 'minute', 'täglich', 'wöchentlich', 'stündlich'],
    fr: ['temps', 'heure', 'minute', 'quotidien', 'hebdomadaire', 'toutes les heures']
  },
  inspection: {
    en: ['inspection', 'inspect', 'check', 'verify', 'audit', 'control'],
    no: ['inspeksjon', 'inspiser', 'sjekk', 'verifiser', 'revisjon', 'kontroll'],
    sv: ['inspektion', 'inspektera', 'kontroll', 'verifiera', 'revision'],
    da: ['inspektion', 'inspicere', 'kontrol', 'verificere', 'revision'],
    fi: ['tarkastus', 'tarkastaa', 'tarkista', 'varmista', 'auditointi'],
    de: ['inspektion', 'prüfung', 'kontrolle', 'verifizieren', 'audit'],
    fr: ['inspection', 'inspecter', 'contrôle', 'vérifier', 'audit']
  }
};

// Language patterns for detection
const LANGUAGE_PATTERNS = {
  no: /\b(og|eller|med|til|fra|av|på|i|er|har|kan|skal|vil|må|tid|temperatur|rengjøring)\b/gi,
  sv: /\b(och|eller|med|till|från|av|på|i|är|har|kan|ska|vill|måste|tid|temperatur|rengöring)\b/gi,
  da: /\b(og|eller|med|til|fra|af|på|i|er|har|kan|skal|vil|må|tid|temperatur|rengøring)\b/gi,
  fi: /\b(ja|tai|kanssa|että|varten|lämpötila|siivous|aika|tarkastus)\b/gi,
  de: /\b(und|oder|mit|zu|von|auf|in|ist|hat|kann|soll|will|muss|zeit|temperatur|reinigung)\b/gi,
  fr: /\b(et|ou|avec|pour|de|sur|dans|est|a|peut|doit|veut|temps|température|nettoyage)\b/gi,
  en: /\b(and|or|with|to|from|on|in|is|has|can|should|will|must|time|temperature|cleaning)\b/gi
};

// Character-based language detection for more accuracy
const LANGUAGE_CHARACTERS = {
  no: /[æøå]/g,
  da: /[æøå]/g,
  sv: /[åäö]/g,
  fi: /[äöå]/g,
  de: /[äöüß]/g,
  fr: /[àáâãäåæçèéêëìíîïñòóôõöùúûüýÿ]/g
};

export const detectLanguageFromText = (text: string): LanguageDetectionResult => {
  const lowerText = text.toLowerCase();
  const scores: Record<string, number> = {};
  
  // Initialize scores
  Object.keys(LANGUAGE_PATTERNS).forEach(lang => {
    scores[lang] = 0;
  });

  // Pattern-based scoring
  Object.entries(LANGUAGE_PATTERNS).forEach(([lang, pattern]) => {
    const matches = lowerText.match(pattern);
    if (matches) {
      scores[lang] += matches.length * 2; // Weight pattern matches higher
    }
  });

  // Character-based scoring
  Object.entries(LANGUAGE_CHARACTERS).forEach(([lang, pattern]) => {
    const matches = text.match(pattern);
    if (matches) {
      scores[lang] += matches.length;
    }
  });

  // HACCP term scoring for domain-specific detection
  Object.entries(MULTILINGUAL_HACCP_TERMS).forEach(([category, languages]) => {
    Object.entries(languages).forEach(([lang, terms]) => {
      terms.forEach(term => {
        if (lowerText.includes(term.toLowerCase())) {
          scores[lang] = (scores[lang] || 0) + 3; // High weight for HACCP terms
        }
      });
    });
  });

  // Find best match
  const sortedLanguages = Object.entries(scores)
    .filter(([, score]) => score > 0)
    .sort(([, a], [, b]) => b - a);

  const detectedLanguage = sortedLanguages.length > 0 ? sortedLanguages[0][0] : 'en';
  const maxScore = sortedLanguages.length > 0 ? sortedLanguages[0][1] : 0;
  const totalWords = text.split(/\s+/).length;
  const confidence = Math.min(maxScore / Math.max(totalWords * 0.1, 1), 1);

  return {
    detectedLanguage,
    confidence,
    supportedLanguages: sortedLanguages.map(([lang]) => lang),
    regionalStandards: getRegionalStandards(detectedLanguage)
  };
};

export const getRegionalStandards = (languageCode: string): string[] => {
  const standards: Record<string, string[]> = {
    en: ['HACCP', 'FDA', 'USDA'],
    no: ['HACCP', 'EU', 'Mattilsynet'],
    sv: ['HACCP', 'EU', 'Livsmedelsverket'],
    da: ['HACCP', 'EU', 'Fødevarestyrelsen'],
    fi: ['HACCP', 'EU', 'Ruokavirasto'],
    de: ['HACCP', 'EU', 'BfR'],
    fr: ['HACCP', 'EU', 'ANSES']
  };
  
  return standards[languageCode] || standards.en;
};

export const getTemperatureUnit = (languageCode: string): '°C' | '°F' => {
  // Most European countries use Celsius, US uses Fahrenheit
  const fahrenheitCountries = ['en-US'];
  return fahrenheitCountries.includes(languageCode) ? '°F' : '°C';
};

export const translateHACCPTerm = (
  term: string, 
  fromLanguage: string, 
  toLanguage: string
): string => {
  const lowerTerm = term.toLowerCase().trim();
  
  // Find the term in the source language
  for (const [category, languages] of Object.entries(MULTILINGUAL_HACCP_TERMS)) {
    const sourceTerms = languages[fromLanguage as keyof typeof languages];
    const targetTerms = languages[toLanguage as keyof typeof languages];
    
    if (sourceTerms && targetTerms) {
      const termIndex = sourceTerms.findIndex(t => 
        lowerTerm.includes(t.toLowerCase())
      );
      
      if (termIndex !== -1 && targetTerms[termIndex]) {
        return targetTerms[termIndex];
      }
    }
  }
  
  return term; // Return original if no translation found
};

export const getUserLanguageContext = async (userId: string) => {
  try {
    // Get user preferences
    const { data: preferences } = await supabase
      .from('user_preferences')
      .select('language_code, time_zone')
      .eq('user_id', userId)
      .maybeSingle();

    // Get browser language as fallback
    const browserLanguages = navigator.languages || [navigator.language];
    const browserLang = browserLanguages[0]?.split('-')[0] || 'en';

    return {
      preferredLanguage: preferences?.language_code || browserLang,
      timezone: preferences?.time_zone || 'UTC',
      browserLanguages: browserLanguages.map(lang => lang.split('-')[0])
    };
  } catch (error) {
    console.error('Failed to get user language context:', error);
    return {
      preferredLanguage: 'en',
      timezone: 'UTC',
      browserLanguages: ['en']
    };
  }
};

export const normalizeColumnHeaders = (
  headers: string[], 
  sourceLanguage: string
): string[] => {
  return headers.map(header => {
    // Convert common column headers to English for processing
    const normalized = translateHACCPTerm(header, sourceLanguage, 'en');
    return normalized || header;
  });
};